import { browserId, clientHeaders } from './device';
import { attributionHandoff, isPwaAttributionStartup } from './attributionHandoff';

export { attributionHandoff } from './attributionHandoff';

const CLICK_KEY = 'sorteia:attributionClickId';
const CAPTURE_KEY = 'sorteia:attributionCapture';
async function captureInstall(confirmedPwa=false) {
  try {
    const clickId=localStorage.getItem(CLICK_KEY);if(!clickId)return;
    const mode=(clientHeaders() as Record<string,string>)['X-Sorteia-Client-Mode'];
    const platform=confirmedPwa?'PWA':mode==='apk'?'APK':mode==='pwa'?'PWA':'WEB';
    await fetch('/api/v1/attribution/install',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({clickId,browserId:browserId(),platform})});
  }catch{/* Telemetry cannot interrupt player startup. */}
}
if(typeof window!=='undefined')window.addEventListener('appinstalled',()=>void captureInstall(true));

function clickIdentity(storage: Storage, key: string) {
  try {let id=storage.getItem(key);if(!id){id=crypto.randomUUID();storage.setItem(key,id);}return id;}catch{return '';}
}

function cookie(name: string) {
  const prefix = `${name}=`;
  return document.cookie.split(';').map((item) => item.trim()).find((item) => item.startsWith(prefix))?.slice(prefix.length) || '';
}

function params() {
  const query = new URLSearchParams(location.search);
  const hashQuery = location.hash.includes('?')
    ? new URLSearchParams(location.hash.slice(location.hash.indexOf('?') + 1))
    : new URLSearchParams();
  const value = (key: string) => query.get(key) || hashQuery.get(key) || '';
  return { query, value };
}

function consumeAttributionHandoff() {
  const handoff = attributionHandoff(location.search);
  if (!handoff.clickId) return '';
  localStorage.setItem(CLICK_KEY, handoff.clickId);
  localStorage.setItem(CAPTURE_KEY, `${location.host.toLowerCase()}::`);
  const cleanUrl = `${location.pathname}${handoff.cleanSearch}${location.hash}`;
  history.replaceState(history.state, '', cleanUrl);
  return handoff.clickId;
}

/** Capture first-party delivery parameters before registration; repeated renders are idempotent. */
export async function captureDeliveryAttribution() {
  try {
    if (consumeAttributionHandoff()) {await captureInstall();return;}
    if (isPwaAttributionStartup(location.search, localStorage.getItem('sorteia:pwaAttribution'), localStorage.getItem(CLICK_KEY))) {
      localStorage.removeItem('sorteia:pwaAttribution');
      localStorage.setItem(CAPTURE_KEY, `${location.host.toLowerCase()}::`);
      await captureInstall();
      return;
    }
  } catch {
    // Storage/history may be unavailable in private mode; normal capture can still proceed.
  }
  const { value } = params();
  const linkCode = (value('delivery') || value('dl')).trim().toUpperCase();
  if (linkCode && !/^[A-Z][A-Z0-9_-]{1,31}$/.test(linkCode)) return;
  // A standalone root launch cannot identify one of several links on this domain.
  // Retain a known click, but never invent attribution from the domain's default link.
  if (!linkCode && (clientHeaders() as Record<string,string>)['X-Sorteia-Client-Mode'] === 'pwa') {await captureInstall();return;}
  if (linkCode) {
    // Never submit an earlier campaign's click while the new capture is pending or fails.
    try {localStorage.removeItem(CLICK_KEY);localStorage.removeItem('sorteia:pwaAttribution');} catch {}
  }
  const clidKey = ['click_id','fbclid','gclid','ttclid','msclkid'].find(key=>!!value(key));
  const signature = `${linkCode || location.host.toLowerCase()}:${clidKey ? clidKey+'='+value(clidKey) : ''}:${value('utm_campaign')}`;
  try {
    if (!linkCode && !['click_id','fbclid','gclid','ttclid','msclkid','utm_source','utm_campaign'].some(key=>!!value(key)) && localStorage.getItem(CAPTURE_KEY) === signature && localStorage.getItem(CLICK_KEY)) {await captureInstall();return;}
    const response = await fetch('/api/v1/attribution/click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        linkCode,
        browserId: browserId(),
        landingPage: location.href, referrer: document.referrer,
        deviceId: clickIdentity(localStorage,'sorteia:deviceId'), sessionId: clickIdentity(sessionStorage,'sorteia:clickSessionId'),
        clidKey: clidKey || 'fbclid',
        clidValue: value(clidKey || 'fbclid'),
        fbclid: value('fbclid'),
        fbc: cookie('_fbc'),
        fbp: cookie('_fbp'),
        utmSource: value('utm_source'),
        utmMedium: value('utm_medium'),
        utmCampaign: value('utm_campaign'),
        utmContent: value('utm_content'),
        utmTerm: value('utm_term'),
      }),
    });
    if (!response.ok) return;
    const result = await response.json() as { clickId?: string };
    if (!result.clickId) return;
    localStorage.setItem(CLICK_KEY, result.clickId);
    localStorage.setItem(CAPTURE_KEY, signature);
    await captureInstall();
  } catch {
    // Attribution must not block the player app; an explicit source can retry on the next load.
  }
}
